import { Add, Delete } from "@mui/icons-material";
import {
  Box, Button, FormControl, MenuItem, Select, Skeleton,
  TextField, Tooltip,
} from "@mui/material";
import IconButton from "components/icon-button";
import { useState } from "react";
import { useStyles } from "./styles";
import { removeExpecialCharactersFromCPF, cpfInputMask } from "utils/cpf";
import { AxiosError } from "axios";
import { useAlert } from "contexts/alert";
import api from "services/api";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

interface Item {
  id: string;
  cpf: string;
  name: string;
  birthday: string;
  type: string;
  cpfHolder: string;
  isOptant: boolean | null;
  plan: string;
  plans: string[];
  date: string;
  reason: string;
  /** Spec 3 §6.2: tipo de exceção. O cancelamento de admissão não é decisão de optante. */
  kind: "OPTANT" | "ADMISSION_CANCELLATION";
}

interface Props {
  contractType: "COMPULSORIO" | "LIVRE_ADESAO";
}

const BLANK_ITEM = (): Item => ({
  id: crypto.randomUUID(),
  cpf: "", name: "", birthday: "", type: "", cpfHolder: "",
  isOptant: null, plan: "", plans: [], date: "", reason: "", kind: "OPTANT",
});

export function ExcecaoView({ contractType }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  const { workspaceActivityId } = useParams() as { workspaceActivityId: string };
  const { openAlert } = useAlert();
  const classes = useStyles();

  function update(id: string, patch: Partial<Item>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }

  async function handleChangeCPF(id: string, value: string) {
    value = removeExpecialCharactersFromCPF(value);
    update(id, { cpf: value });
    if (value.length !== 11) return;
    try {
      setLoading((prev) => ({ ...prev, [id]: true }));
      const { data } = await api.post("/apps/excecao/find-by-tax-payer-id", {
        taxPayerId: value,
        workspaceActivityId,
      });
      // O backend retorna um vínculo por CPF (array). O guard de objeto único
      // cobre a janela de deploy em que o backend antigo ainda responde.
      const vinculos = Array.isArray(data.data) ? data.data : [data.data];
      if (vinculos.length === 0) return;

      const person = vinculos[0];
      update(id, {
        name: person.name,
        birthday: person.birthDate,
        type: !person.type ? "" : person.type === "employee" ? "Titular" : "Dependente",
        cpfHolder: person.employeeTaxPayerId,
        cpf: person.taxPayerId,
        plans: person.plans ?? [],
      });
    } catch (error) {
      if (error instanceof AxiosError) openAlert(error.response?.data.data.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  }

  const onSubmit = async () => {
    try {
      setIsSaving(true);

      // canSave already filters incomplete rows; this guard is defensive — a row
      // with isOptant=null must never be sent (`!null === true` would silently
      // submit it as an opt-out).
      const payload = items
        .filter((item) => item.kind === "ADMISSION_CANCELLATION" || item.isOptant !== null)
        .map((item) => item.kind === "ADMISSION_CANCELLATION"
          // Spec 3 §6.2, a volta: a corretora confirma o cancelamento junto à operadora.
          ? {
            taxPayerId: removeExpecialCharactersFromCPF(item.cpf),
            admissionCancellationConfirmed: true,
            ...(item.reason && { reason: item.reason }),
          }
          : {
            taxPayerId: removeExpecialCharactersFromCPF(item.cpf),
            isNotOptant: item.isOptant === false,
            ...(item.isOptant === true && item.plan && { plan: item.plan }),
            ...(item.isOptant === true && item.date && { effectiveDate: item.date }),
            ...(item.isOptant === false && item.date && { expireDate: dayjs(item.date).format("DD/MM/YYYY") }),
            ...(item.isOptant === false && item.reason && { reason: item.reason }),
          });

      await api.post(`/v2/apps/exception/${workspaceActivityId}`, { payload, contractType });

      setItems([]);
      openAlert("Exceções salvas com sucesso", "success");
    } catch (error) {
      if (error instanceof AxiosError) openAlert(error.response?.data.data.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = items.length > 0;
  const canSave =
    hasChanges &&
    items.every((it) => removeExpecialCharactersFromCPF(it.cpf).length === 11) &&
    items.every((it) => it.kind === "ADMISSION_CANCELLATION" || it.isOptant !== null);

  function renderReadOnlyCells(item: Item) {
    const isLoading = !!loading[item.id];
    return (
      <>
        <td className={classes.tableOthersSize}>
          {isLoading ? <Skeleton variant="rounded" width={140} height={20} /> : (
            <input value={item.name} readOnly className={classes.searchInput} />
          )}
        </td>
        <td className={classes.tableOthersSize}>
          {isLoading ? <Skeleton variant="rounded" width={140} height={20} /> : (
            <input value={item.birthday} readOnly className={classes.searchInput} />
          )}
        </td>
        <td className={classes.tableOthersSize}>
          {isLoading ? <Skeleton variant="rounded" width={140} height={20} /> : (
            <input value={item.type} readOnly className={classes.searchInput} />
          )}
        </td>
        <td className={classes.tableOthersSize}>
          {isLoading ? <Skeleton variant="rounded" width={140} height={20} /> : (
            <input value={item.cpfHolder} readOnly className={classes.searchInput} />
          )}
        </td>
      </>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <div className={classes.addButton}>
        <Tooltip title="Adicionar">
          <Add onClick={() => setItems((prev) => [...prev, BLANK_ITEM()])} style={{ cursor: "pointer" }} />
        </Tooltip>
      </div>

      {items.length === 0 ? (
        <Box className={classes.initialBase}>
          <span className={classes.initialBaseText}>Clique em</span>
          <Add />
          <span className={classes.initialBaseText}>para adicionar uma exceção</span>
        </Box>
      ) : (
        <table className={classes.tableBase}>
          <thead className={classes.tableHeader}>
            <tr>
              <th className={classes.tableOthersSize}>CPF</th>
              <th className={classes.tableOthersSize}>Nome</th>
              <th className={classes.tableOthersSize}>Data de nascimento</th>
              <th className={classes.tableOthersSize}>Tipo</th>
              <th className={classes.tableOthersSize}>Vinculado ao CPF</th>
              <th className={classes.tableOthersSize}>Tipo de exceção</th>
              <th className={classes.tableOthersSize}>Optante?</th>
              <th className={classes.tableOthersSize}>Plano</th>
              <th className={classes.tableOthersSize}>Data</th>
              <th className={classes.tableOthersSize}>Motivo</th>
              <th className={classes.tableActionSize} />
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {items.map((item) => (
              <tr key={item.id}>
                <td className={classes.tableOthersSize}>
                  <input
                    value={cpfInputMask(item.cpf)}
                    className={classes.searchInput}
                    onChange={(e) => handleChangeCPF(item.id, e.target.value)}
                    placeholder="Digite o CPF"
                  />
                </td>
                {renderReadOnlyCells(item)}
                <td className={classes.tableOthersSize}>
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 160 }}>
                    <Select
                      value={item.kind}
                      onChange={(e) => update(item.id, { kind: e.target.value as Item["kind"], isOptant: null })}
                      className={classes.inputDate}
                    >
                      <MenuItem value="OPTANT">Optante</MenuItem>
                      <MenuItem value="ADMISSION_CANCELLATION">Cancelamento de admissão confirmado</MenuItem>
                    </Select>
                  </FormControl>
                </td>
                <td className={classes.tableOthersSize}>
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 80 }}>
                    <Select
                      value={item.isOptant === null ? "" : String(item.isOptant)}
                      disabled={item.kind === "ADMISSION_CANCELLATION"}
                      displayEmpty
                      onChange={(e) => update(item.id, { isOptant: e.target.value === "true" })}
                      className={classes.inputDate}
                    >
                      <MenuItem value="" disabled><em>—</em></MenuItem>
                      <MenuItem value="true">Sim</MenuItem>
                      <MenuItem value="false">Não</MenuItem>
                    </Select>
                  </FormControl>
                </td>
                <td className={classes.tableOthersSize}>
                  <FormControl
                    variant="standard"
                    sx={{ m: 1, minWidth: 120 }}
                    disabled={item.kind === "ADMISSION_CANCELLATION" || item.isOptant !== true || item.plans.length === 0}
                  >
                    <Select
                      value={item.plan}
                      displayEmpty
                      onChange={(e) => update(item.id, { plan: e.target.value })}
                      className={classes.inputDate}
                    >
                      <MenuItem value="" disabled>
                        <em>{item.plans.length === 0 ? "—" : "Selecione"}</em>
                      </MenuItem>
                      {item.plans.map((plan) => (
                        <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </td>
                <td className={classes.tableOthersSize}>
                  <Tooltip title={item.isOptant === true ? "Data de início" : item.isOptant === false ? "Data de fim de plano" : ""}>
                    <span>
                      <TextField
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="filled"
                        fullWidth
                        margin="normal"
                        disabled={item.kind === "ADMISSION_CANCELLATION" || item.isOptant === null}
                        value={item.date}
                        onChange={(e) => update(item.id, { date: e.target.value })}
                        className={classes.inputDate}
                      />
                    </span>
                  </Tooltip>
                </td>
                <td className={classes.tableOthersSize}>
                  <TextField
                    variant="standard"
                    fullWidth
                    placeholder={item.kind === "ADMISSION_CANCELLATION" ? "Referência da operadora (opcional)" : "Motivo (opcional)"}
                    disabled={item.kind !== "ADMISSION_CANCELLATION" && item.isOptant !== false}
                    value={item.reason}
                    onChange={(e) => update(item.id, { reason: e.target.value })}
                    inputProps={{ style: { fontSize: "0.875rem" } }}
                  />
                </td>
                <td className={classes.tableOthersSize}>
                  <IconButton
                    icon={Delete}
                    isWarning
                    onClick={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                    tooltip="Remover"
                    isDisabled={isSaving}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Box className={classes.actions}>
        <Button
          type="button"
          variant="outlined"
          disabled={!hasChanges || isSaving}
          className={classes.cancelButton}
          onClick={() => setItems([])}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          variant="contained"
          size="small"
          disabled={!canSave || isSaving}
          className={classes.submitButton}
          onClick={onSubmit}
        >
          Salvar
        </Button>
      </Box>
    </div>
  );
}
